/** WildEmitter.js is a slim little event emitter by @henrikjoreteg largely based on @visionmedia's Emitter from UI Kit. */

type Callback = Function & { _groupName?: string }

export default class WildEmitter {
    private callbacks: { [event: string]: Callback[] } = {};

    /** Unbinds an entire group */
    releaseGroup(groupName: string): this {
        for (const item in this.callbacks) {
            const handlers = this.callbacks[item];
            for (let i = 0, len = handlers.length; i < len; i++) {
                if (handlers[i]._groupName === groupName) {
                    // remove it and shorten the array we're looping through
                    handlers.splice(i, 1);
                    i--;
                    len--;
                }
            }
        }

        return this;
    }

    /** registered callbacks. */
    off(event: string, fn?: Callback): this {
        const callbacks = this.callbacks[event];

        if (!callbacks) {
            return this;
        }

        // remove all handlers
        if (fn === undefined) {
            delete this.callbacks[event];
            return this;
        }

        // remove specific handler
        const i = callbacks.indexOf(fn);
        if (i !== -1) {
            callbacks.splice(i, 1);
            if (callbacks.length === 0) {
                delete this.callbacks[event];
            }
        }

        return this;
    }

    /** also calls any `*` handlers */
    emit(event: string, ...args: any[]) {
        const callbacks = this.callbacks[event];
        const specialCallbacks = this.getWildcardCallbacks(event);

        if (callbacks) {
            const listeners = callbacks.slice();
            for (let i = 0, len = listeners.length; i < len; ++i) {
                if (!listeners[i]) {
                    break;
                }
                listeners[i].apply(this, args);
            }
        }

        if (specialCallbacks) {
            const listeners = specialCallbacks.slice();
            for (let i = 0, len = listeners.length; i < len; ++i) {
                if (!listeners[i]) {
                    break;
                }
                listeners[i].apply(this, [event].concat(args));
            }
        }

        return this;
    }

    /** Helper for finding special wildcard event handlers that match the event */
    getWildcardCallbacks(eventName: string): Callback[] {
        let result: Callback[] = [];

        for (const item in this.callbacks) {
            const split = item.split("*");
            if (item === "*" || (split.length === 2 && eventName.slice(0, split[0].length) === split[0])) {
                result = result.concat(this.callbacks[item]);
            }
        }
        return result;
    }

    /** Listen on the given `event` with `fn`. Store a group name if present. */
    on(event: string, fn: Callback): this;
    /** Listen on the given `event` with `fn`. Store a group name if present. */
    on(event: string, groupName: string, fn: Callback): this
    /** Listen on the given `event` with `fn`. Store a group name if present. */
    on(event: string, groupOrCb: string | Callback, cb?: Callback): this {
        const {group, func} = this.extractArgs(groupOrCb, cb);
        func._groupName = group;
        (this.callbacks[event] = this.callbacks[event] || []).push(func);

        return this;
    }

    /** Adds an `event` listener that will be invoked a single time then automatically removed. */
    once(event: string, fn: Callback): this
    /** Adds an `event` listener that will be invoked a single time then automatically removed. */
    once(event: string, groupName: string, fn: Callback): this
    /** Adds an `event` listener that will be invoked a single time then automatically removed. */
    once(event: string, groupOrCb: string | Callback, cb?: Callback): this {
        const {group, func} = this.extractArgs(groupOrCb, cb);

        const on: Callback = (...args: any[]) => {
            this.off(event, on);
            func.apply(this, args);
        };

        if (group === undefined) {
            this.on(event, on);
        } else {
            this.on(event, group, on);
        }

        return this;
    }

    private extractArgs(groupName: string | Callback, fn?: Callback): {
        func: Callback;
        group?: string
    } {
        if (fn === undefined) {
            return {group: undefined, func: groupName as Callback};
        }

        return {group: groupName as string, func: fn};
    }
}
